import React from 'react';
import toFinite from 'lodash/toFinite';
import { Tooltip } from '@chaser/components';
import { localeByCountry } from './locales';
import { produce } from 'immer';
import {
  ddCurrencyMapping,
  ddPaymentFailedErrorsMapping,
  DebitPaymentTypes,
  roles,
} from './constants';
import collections from './collections';
import { Integrations } from './constants';
import { hasRole } from '../components/FeatureGate/RoleGate';

const getInvoiceUrl = invoiceId => `/invoice/${invoiceId}`;

const getCustomerUrl = customerId => `/customer/${customerId}`;

const getStatusFromInvoice = invoice => {
  const { Status, collector, isPaymentPending } = invoice;

  return {
    isPaid: Status === 'PAID',
    isCollecting: collector?.status === 'COLLECTING',
    isPendingCollection: collector?.status === 'PENDING',
    isWaitingContract: collector?.status === 'WAITING_CONTRACT',
    isPaymentPendingDirectDebit: isPaymentPending && Status === 'AUTHORISED',
    isInCollection: collector?.status === 'COLLECTING',
    isValidForCollections: collections.validate([invoice]).isValid,
  };
};
const toCurrency = (amount, currencyCode = 'GBP', style = 'currency') => {
  const formatter = new Intl.NumberFormat('en-US', {
    style,
    currency: currencyCode,
    minimumFractionDigits: Math.floor(amount) === amount ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return amount === 0 || toFinite(amount) ? formatter.format(amount) : '';
};

function formatLocalCurrency(amount, countryCode, currencyCode, style = 'currency', options) {
  if (amount == null) return '';
  return amount.toLocaleString(localeByCountry[countryCode] || countryCode || 'en-US', {
    style,
    currency: currencyCode,
    numberingSystem: 'latn',
    minimumFractionDigits: Math.floor(amount) === amount ? 0 : 2,
    maximumFractionDigits: 2,
    ...options,
  });
}

function getLocalCurrencySymbol(countryCode, currencyCode) {
  return (11)
    .toLocaleString(localeByCountry[countryCode] || countryCode || 'en-US', {
      style: 'currency',
      currency: currencyCode,
      numberingSystem: 'latn',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace('11', '')
    .trim();
}

function formatLocalNumber(
  number,
  countryCode,
  { minimumFractionDigits, maximumFractionDigits } = {},
) {
  if (number == null) return '';
  return number.toLocaleString(localeByCountry[countryCode] || countryCode || 'en-US', {
    numberingSystem: 'latn',
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

const round = (value, decimals) => +value.toFixed(decimals);

const getNextValueSuffixByName = (prefix, arr) => {
  let val;
  let count = arr.length + 1;
  while (!val) {
    const nmeValue = count;
    const hasNameAlready = arr.find(({ name }) => name === `${prefix} ${nmeValue}`);
    if (!hasNameAlready) {
      val = nmeValue;
    }
    count += 1;
  }

  return `${prefix} ${val}`;
};

function shallowEqual(objA, objB) {
  if (objA === objB) {
    return true;
  }

  if (!objA || !objB) {
    return false;
  }

  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);
  const len = aKeys.length;

  if (bKeys.length !== len) {
    return false;
  }

  for (let i = 0; i < len; i++) {
    const key = aKeys[i];
    const valA = objA[key];
    const valB = objB[key];

    if (Array.isArray(valA) && Array.isArray(valB)) {
      if (valA.length !== valB.length) {
        return false;
      }

      for (let j = 0; j < valA.length; j++) {
        if (valA[j] !== valB[j]) {
          return false;
        }
      }
    } else if (valA !== valB) {
      return false;
    }
  }

  return true;
}

function ellipsisTooltip(str, len, position = 'right') {
  return str && str.length > len ? (
    <Tooltip position={position} content={str}>
      <span>
        {str.substring(0, len)}
        ...
      </span>
    </Tooltip>
  ) : (
    str
  );
}

function parseQueryString(qs) {
  const search = new URLSearchParams(qs);
  const state = {};
  Array.from(search.entries()).forEach(([k, v]) => {
    state[k] = v;
  });
  return state;
}

const fileUnits = ['B', 'kB', 'MB', 'GB', 'TB'];
const fileSizeFormatter = new Intl.NumberFormat('en', { maximumSignificantDigits: 3 });
function formatFileSize(size) {
  let i = 0;
  let amount = size;
  while (amount > 1024 && i < fileUnits.length - 1) {
    i++;
    amount /= 1024;
  }
  return `${fileSizeFormatter.format(amount)}${fileUnits[i]}`;
}

function createDeferredPromise() {
  let resolve;
  let reject;
  const promise = new Promise((yay, nay) => {
    reject = nay;
    resolve = yay;
  });
  promise.resolve = resolve;
  promise.reject = reject;

  return promise;
}

function immerReducer(actions) {
  return produce((draft, { type, payload }) => {
    if (!actions[type]) throw new Error(`Unknown action: '${type}'`);
    actions[type](draft, payload);
  });
}

function toTitleCase(str) {
  if (!str) return '';
  return str.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

const isDirectDebitConfigured = (customer, org) => {
  if (!customer || !customer.directDebit) return false;
  if (Object.keys(customer.directDebit) === 0) return false;

  const propertyDD =
    ddCurrencyMapping[org.BaseCurrency?.toLowerCase()]?.contactDDMandatePropertyAt || null;
  if (!propertyDD) return false;

  if (customer.directDebit[propertyDD]) return true;

  return false;
};

const isLastInvoicePaymentFailedForDD = invoice => {
  if (invoice.paymentHistory && invoice.paymentHistory.length > 0) {
    const lastPayment = invoice.paymentHistory[invoice.paymentHistory.length - 1];
    return (
      lastPayment.event_data?.type?.includes('failed') &&
      Object.keys(DebitPaymentTypes).includes(lastPayment.event_data?.paymentMethodType)
    );
  }

  return false;
};

const isMaxPaymentRetryLimitExceeded = invoice => {
  if (!invoice.paymentHistory) return false;

  const paymentHistory = invoice.paymentHistory.filter(
    history =>
      history.event_data?.type === 'payment_intent.payment_failed' &&
      history.event_data?.paymentMethodType ===
        ddCurrencyMapping[invoice.CurrencyCode.toLowerCase()]?.stripeDDMethod,
  );

  if (paymentHistory && paymentHistory.length >= (invoice.paymentMaxRetries || 2)) {
    return true;
  }

  return false;
};

const getLastPaymentInformation = invoice => {
  const { paymentHistory } = invoice;

  if (!paymentHistory || paymentHistory.length === 0) return null;

  const lastPayment = paymentHistory[paymentHistory.length - 1];
  const { event_data } = lastPayment;

  return {
    date: lastPayment.date,
    provider: lastPayment.provider,
    type: event_data?.type,
    paymentMethod: event_data?.paymentMethodType,
    errorCode: event_data?.errorCode,
    errorMessage: ddPaymentFailedErrorsMapping[event_data?.errorCode],
  };
};

function isStaffMember(user) {
  if (typeof user === 'string') {
    if (user.includes('@chaserhq.com')) return true;
    if (user.includes('@chaser.io')) return true;
    if (user.includes('@chasercollections.com')) return true;
  }

  if (typeof user === 'object') {
    if (user?.emails[0].address.endsWith('@chaserhq.com')) return true;
    if (user?.emails[0].address.endsWith('@chaser.io')) return true;
    if (user?.emails[0].address.endsWith('@chasercollections.com')) return true;
  }

  if (hasRole(user, [roles.admin, roles.controller, roles.tester])) return true;

  return false;
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function isInvoicePdfAvailable(accountingSoftware, hasInvoicePdfLink) {
  const REQUIRES_INVOICE_PDF = [Integrations.SAGE200, Integrations.OPENAPI, Integrations.CSV];

  const ALLOWED_ACCOUNTING_SOFTWARE = [
    Integrations.XERO,
    Integrations.DYNAMICS,
    Integrations.FREEAGENT,
    Integrations.FRESHBOOKS,
    Integrations.QUICKBOOKS,
    Integrations.ACCOUNTSIQ,
    Integrations.SAGE,
    Integrations.D365BC,
    Integrations.ODOO,
    Integrations.NETSUITE,
    Integrations.SAGE200,
    Integrations.SAGEINTACCT,
    Integrations.OPENAPI,
    Integrations.CSV,
    Integrations.STRIPE,
  ];

  if (REQUIRES_INVOICE_PDF.includes(accountingSoftware) && !hasInvoicePdfLink) {
    return false;
  }

  return ALLOWED_ACCOUNTING_SOFTWARE.includes(accountingSoftware);
}

const parseTimestamp = dateString => {
  const timestamp = parseInt(dateString, 10);

  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatNegativeNumber = (value, formatFunc) => {
  if (value < 0) {
    return `(${formatFunc(Math.abs(value))})`;
  }
  return formatFunc(value);
};

const getChannelName = (channel, isAutoCall = false) => {
  switch (channel) {
    case 'email':
      return 'Email';
    case 'phone_call':
      return isAutoCall ? 'Auto-call' : 'Phone call';
    case 'sms':
      return 'SMS';
    case 'letter':
      return 'Letter';
    default:
      return 'Unknown';
  }
};

function constructCaseIdReturnUrl(caseId, path = '/receivables/collections/in-progress') {
  const returnUrl = new URL(path, window.location.origin);
  returnUrl.searchParams.set('caseId', caseId);
  returnUrl.searchParams.set('signed', 'true');
  return returnUrl.toString();
}

export {
  isStaffMember,
  toTitleCase,
  toCurrency,
  getNextValueSuffixByName,
  getInvoiceUrl,
  getCustomerUrl,
  shallowEqual,
  ellipsisTooltip,
  parseQueryString,
  formatFileSize,
  round,
  formatLocalCurrency,
  formatLocalNumber,
  getLocalCurrencySymbol,
  createDeferredPromise,
  immerReducer,
  getStatusFromInvoice,
  isDirectDebitConfigured,
  isLastInvoicePaymentFailedForDD as isLastInvoicePaymentFailed,
  getLastPaymentInformation,
  isMaxPaymentRetryLimitExceeded,
  capitalizeFirstLetter,
  isInvoicePdfAvailable,
  parseTimestamp,
  formatNegativeNumber,
  getChannelName,
  constructCaseIdReturnUrl,
};
